import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CourseCard = ({ course, onEnroll }) => {
  const getDifficultyColor = (level) => {
    switch (level) {
      case 'Beginner': return 'text-success bg-success/10';
      case 'Intermediate': return 'text-warning bg-warning/10';
      case 'Advanced': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  return (
    <div className="holographic-card group cursor-pointer transition-all duration-300 hover:scale-[1.02]">
      <div className="relative overflow-hidden rounded-t-lg h-48">
        <Image
          src={course.thumbnail}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
        
        {/* Course Badge */}
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
            {course.difficulty}
          </span>
        </div>

        {/* Duration Badge */}
        <div className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm rounded-full px-2 py-1">
          <span className="text-xs text-foreground font-medium">{course.duration}</span>
        </div>

        {/* Progress Overlay */}
        {course.progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted/50">
            <div 
              className="h-full bg-accent"
              style={{ width: `${course.progress}%` }}
            ></div>
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Course Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-accent transition-colors duration-300">
              {course.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {course.description}
            </p>
          </div>
          <div className="ml-3 flex items-center space-x-1 text-warning">
            <Icon name="Star" size={14} fill="currentColor" />
            <span className="text-sm font-medium">{course.rating}</span>
          </div>
        </div>

        {/* Instructor */}
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-6 h-6 rounded-full overflow-hidden">
            <Image
              src={course.instructor.avatar}
              alt={course.instructor.name}
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-sm text-muted-foreground">{course.instructor.name}</span>
          <span className="text-xs text-muted-foreground">• {course.instructor.company}</span>
        </div>

        {/* Course Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4 py-3 border-t border-b border-border/50">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 text-accent mb-1">
              <Icon name="Users" size={14} />
              <span className="text-sm font-medium">{course.enrolled}</span>
            </div>
            <span className="text-xs text-muted-foreground">Enrolled</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 text-success mb-1">
              <Icon name="CheckCircle" size={14} />
              <span className="text-sm font-medium">{course.completionRate}%</span>
            </div>
            <span className="text-xs text-muted-foreground">Completion</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 text-cyber-purple mb-1">
              <Icon name="Briefcase" size={14} />
              <span className="text-sm font-medium">{course.jobPlacement}%</span>
            </div>
            <span className="text-xs text-muted-foreground">Job Rate</span>
          </div>
        </div>

        {/* Technologies */}
        <div className="flex flex-wrap gap-2 mb-4">
          {course.technologies.map((tech, index) => (
            <span 
              key={index}
              className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded-md"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Action Button */}
        <Button
          variant={course.progress > 0 ? "outline" : "default"}
          fullWidth
          iconName={course.progress > 0 ? "Play" : "BookOpen"}
          iconPosition="left"
          onClick={() => onEnroll(course)}
          className="morphing-cta"
        >
          {course.progress > 0 ? "Continue Learning" : "Start Course"}
        </Button>
      </div>
    </div>
  );
};

export default CourseCard;